import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { createEmailGroup } from '../../graphql/email-groups/create-email-group';
import { queryForEmailGroups } from '../../graphql/email-groups/query-for-email-groups';
import { EmailGroupInput } from '../../model/admin/email-group-input';
import { updateEmailGroup } from '../../graphql/email-groups/update-email-group';
import { deleteEmailGroup } from '../../graphql/email-groups/delete-email-group';
import { queryForRolesAndClaims } from '../../graphql/contacts/query-for-roles-and-claims';
import { getAvailableClaims } from '../../graphql/contacts/get-available-claims';
import { updateRole } from '../../graphql/contacts/update-role';
import { UpdateRoleInput } from '../../model/admin/update-role-input';
import { queryForRolesAndBusinessUnits } from '../../graphql/contacts/query-for-roles-and-business-units';
import { getAvailableBusinessUnits } from '../../graphql/contacts/get-available-business-units';
import {
  Claim,
  ClaimConnection,
  RoleSortOrQuery,
  UpdateClaimInput,
} from '../../model/admin/claim';
import { BaseAppSyncService } from '../base-app-sync.service';
import { queryForClaimsAndRoles } from '../../graphql/contacts/query-for-claims-and-roles';
import { updateClaim } from '../../graphql/contacts/update-claim';
import { Role, RoleConnection } from '../../model/admin/role';
import { copyClaims } from '../../graphql/contacts/copy-claims';
import { CompanyDataTableInput } from 'src/app/model/admin/company-data-table-input';
import { updateCompany } from 'src/app/graphql/companies/update-company';
import { AdminBuilderService } from '../builder/admin-builder.service';
import { Company, CompanyConnection } from 'src/app/model/contacts/company';
import { getCompanyHistories } from 'src/app/graphql/companies/get-company-histories';
import { buildAuditHistory } from 'src/app/core/functions/common-functions';
import { Audit } from 'src/app/model/common/audit';
import { getCompanies } from 'src/app/graphql/companies/get-companies';
import { queryForCompanies } from 'src/app/graphql/companies/query-for-companies';
import { deleteCompany } from 'src/app/graphql/companies/delete-company';
import { createCompany } from 'src/app/graphql/companies/create-company';
import { allowCompanyDelete } from 'src/app/graphql/companies/allow-company-delete';
import { BusinessUnit } from 'src/app/model/admin/business-unit';
import { EmailGroup } from 'src/app/model/admin/email-group';

@Injectable({
  providedIn: 'root',
})
export class AdminApiService extends BaseAppSyncService {
  protected _adminBuilder = inject(AdminBuilderService);

  queryForEmailGroups(
    limit: number | null,
    page: number,
    query: any,
    sort: any
  ): Observable<EmailGroup[]> {
    return this.query(
      queryForEmailGroups,
      { limit, page, query, sort },
      (items: any[]) => items.map((i) => this._adminBuilder.buildEmailGroup(i)),
      'queryForEmailGroups'
    );
  }

  createEmailGroup(
    emailGroupInput: EmailGroupInput | null
  ): Observable<EmailGroup> {
    return this.mutate(
      createEmailGroup,
      { input: emailGroupInput },
      (i: any) => this._adminBuilder.buildEmailGroup(i),
      'createEmailGroup'
    );
  }

  updateEmailGroup(
    id: string | undefined,
    emailGroupInput: EmailGroupInput | null
  ): Observable<EmailGroup> {
    return this.mutate(
      updateEmailGroup,
      { id, changes: emailGroupInput },
      (i: any) => this._adminBuilder.buildEmailGroup(i),
      'updateEmailGroup'
    );
  }

  deleteEmailGroup(id: string): Observable<boolean> {
    return this.mutate(
      deleteEmailGroup,
      { id },
      (i: any) => i,
      'deleteEmailGroup'
    );
  }

  getAvailableClaims(): Observable<Claim[]> {
    return this.query(
      getAvailableClaims,
      {},
      (items: any[]) => items.map((i) => this._adminBuilder.buildClaim(i)),
      'getAvailableClaims'
    );
  }

  getAvailableBusinessUnits(): Observable<BusinessUnit[]> {
    return this.query(
      getAvailableBusinessUnits,
      {},
      (items: any[]) =>
        items.map((i) => this._adminBuilder.buildBusinessUnit(i)),
      'getAvailableBusinessUnits'
    );
  }

  queryForRolesAndClaims(
    limit: number,
    page: number,
    query: RoleSortOrQuery,
    sort: RoleSortOrQuery
  ): Observable<RoleConnection> {
    return this.query(
      queryForRolesAndClaims,
      { limit, page, query, sort },
      (i: any) => this._adminBuilder.buildRoleConnection(i),
      'queryForRolesAndClaims'
    );
  }

  queryForRolesAndBusinessUnits(
    limit: number,
    page: number,
    query: RoleSortOrQuery,
    sort: RoleSortOrQuery
  ): Observable<RoleConnection> {
    return this.query(
      queryForRolesAndBusinessUnits,
      { limit, page, query, sort },
      (i: any) => this._adminBuilder.buildRoleConnection(i),
      'queryForRolesAndBusinessUnits'
    );
  }

  updateRole(
    id: string,
    updateRoleInput: UpdateRoleInput | null
  ): Observable<Role> {
    return this.mutate(
      updateRole,
      { id, input: updateRoleInput },
      (i: any) => this._adminBuilder.buildRole(i),
      'updateRole'
    );
  }

  queryForClaimsAndRoles(
    limit: number,
    page: number,
    query: RoleSortOrQuery,
    sort: RoleSortOrQuery
  ): Observable<ClaimConnection> {
    return this.query(
      queryForClaimsAndRoles,
      { limit, page, query, sort },
      (i: any) => i as ClaimConnection,
      'queryForClaimsAndRoles'
    );
  }

  updateClaim(input: UpdateClaimInput | null): Observable<Claim> {
    return this.mutate(
      updateClaim,
      { input },
      (i: any) => i as Claim,
      'updateClaim'
    );
  }

  copyClaims(sourceRole: string, destinationRole: String): Observable<Role> {
    return this.mutate(
      copyClaims,
      { sourceRole, destinationRole },
      (i: any) => i as Role,
      'copyClaims'
    );
  }

  updateCompany(
    id: string,
    changes: CompanyDataTableInput | null
  ): Observable<Company> {
    return this.mutate(
      updateCompany,
      { id, changes },
      (i: any) => this._adminBuilder.buildCompany(i),
      'updateCompany'
    );
  }

  getCompanyHistories(id: string): Observable<Audit[]> {
    return this.query(
      getCompanyHistories,
      { id },
      (histories: any[]) => buildAuditHistory(histories),
      'getCompanyHistories'
    );
  }

  getCompanies(): Observable<Company[]> {
    return this.query(
      getCompanies,
      {},
      (items: any[]) => items.map((i) => this._adminBuilder.buildCompany(i)),
      'getCompanies',
      'cache-first'
    );
  }

  queryForCompanies(
    limit: number,
    page: number,
    query: CompanyDataTableInput,
    sort: CompanyDataTableInput
  ): Observable<CompanyConnection> {
    return this.query(
      queryForCompanies,
      { limit, page, query, sort },
      (i: any) => this._adminBuilder.buildCompanyConnection(i),
      'queryForCompanies'
    );
  }

  deleteCompany(id: string): Observable<boolean> {
    return this.mutate(deleteCompany, { id }, (i: any) => i, 'deleteCompany');
  }

  createCompany(input: CompanyDataTableInput | null): Observable<Company> {
    return this.mutate(
      createCompany,
      { input },
      (i: any) => this._adminBuilder.buildCompany(i),
      'createCompany'
    );
  }

  allowCompanyDelete(id: string): Observable<boolean> {
    return this.query(
      allowCompanyDelete,
      { id },
      (i: any) => i.value,
      'allowCompanyDelete'
    );
  }
}
